import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Crown, Loader2, AlertTriangle } from 'lucide-react';
import { GrantPremiumDialogProps } from '../types';

/**
 * Конвертирует строку из <input type="date"> ("YYYY-MM-DD") в ISO-строку
 * с временем 23:59:59.999 в локальном часовом поясе. Так избегаем
 * ловушки с `new Date("2026-05-25")`, который JS трактует как UTC-полночь
 * и в Asia/Tashkent (UTC+5) падает на предыдущий день.
 */
function dateInputToIsoEndOfDay(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (!year || !month || !day) return null;
  const local = new Date(year, month - 1, day, 23, 59, 59, 999);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

/** Сегодня в формате YYYY-MM-DD (для атрибута min у <input type="date">). */
function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Через `monthsFromNow` месяцев в формате YYYY-MM-DD (для presets). */
function presetDate(monthsFromNow: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + monthsFromNow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Дата из БД (Date | string) в YYYY-MM-DD для prefill. */
function existingEndToInputDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function GrantPremiumDialog({
  isDark,
  isOpen,
  onOpenChange,
  user,
  isSaving,
  onGrant,
  onRevoke,
}: GrantPremiumDialogProps) {
  const [dateValue, setDateValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => todayIsoDate(), []);
  const hasPremium = !!user?.isSubscribed;
  const currentEnd = user?.subscribedEnd
    ? new Date(user.subscribedEnd)
    : null;

  // Reset формы при каждом открытии диалога: prefill текущей датой если есть,
  // иначе +1 месяц как разумный default.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (user?.subscribedEnd) {
      setDateValue(existingEndToInputDate(user.subscribedEnd));
    } else {
      setDateValue(presetDate(1));
    }
  }, [isOpen, user?.id, user?.subscribedEnd]);

  const handleGrantClick = async () => {
    setError(null);
    if (!dateValue) {
      setError('Pick an end date');
      return;
    }
    const iso = dateInputToIsoEndOfDay(dateValue);
    if (!iso) {
      setError('Invalid date format');
      return;
    }
    const parsed = new Date(iso);
    if (parsed.getTime() <= Date.now()) {
      setError('End date must be in the future');
      return;
    }
    await onGrant(iso);
  };

  const presetOptions: Array<{ label: string; months: number }> = [
    { label: '+1 month', months: 1 },
    { label: '+3 months', months: 3 },
    { label: '+6 months', months: 6 },
    { label: '+1 year', months: 12 },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-md',
          isDark && 'bg-[#1E293B] border-slate-700 text-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            {hasPremium ? 'Manage Premium' : 'Grant Premium'}
          </DialogTitle>
          <DialogDescription className={isDark ? 'text-slate-400' : ''}>
            {user
              ? `For ${user.firstName || user.username}${user.email ? ` (${user.email})` : ''}`
              : 'Set premium subscription end date'}
          </DialogDescription>
        </DialogHeader>

        {hasPremium && currentEnd && (
          <div
            className={cn(
              'flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm',
              isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700',
            )}
          >
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4" />
              <span>
                Premium until <strong>{currentEnd.toLocaleDateString()}</strong>
              </span>
            </div>
            <Badge
              className={cn(
                'border-0',
                currentEnd.getTime() > Date.now()
                  ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300'
                  : 'bg-red-500/20 text-red-600 dark:text-red-300',
              )}
            >
              {currentEnd.getTime() > Date.now() ? 'Active' : 'Expired'}
            </Badge>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label htmlFor="premium-end-date" className={isDark ? 'text-slate-300' : ''}>
              End date
            </Label>
            <Input
              id="premium-end-date"
              type="date"
              value={dateValue}
              min={today}
              onChange={(e) => {
                setDateValue(e.target.value);
                setError(null);
              }}
              className={cn(
                'mt-1.5',
                isDark && 'bg-slate-800 border-slate-700 text-white',
              )}
            />
            <p
              className={cn(
                'text-xs mt-1.5',
                isDark ? 'text-slate-500' : 'text-slate-400',
              )}
            >
              Premium will be active until 23:59 on this day (local time).
            </p>
          </div>

          <div>
            <Label className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
              Quick presets
            </Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {presetOptions.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDateValue(presetDate(p.months));
                    setError(null);
                  }}
                  className={cn(
                    'h-8 rounded-lg',
                    isDark && 'border-slate-700 text-slate-300 hover:bg-slate-800',
                  )}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {error && (
            <div
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm',
                isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700',
              )}
            >
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          {hasPremium ? (
            <Button
              variant="outline"
              onClick={onRevoke}
              disabled={isSaving}
              className={cn(
                'text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
                isDark && 'border-slate-700',
              )}
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Revoke premium
            </Button>
          ) : (
            <span className="hidden sm:block" />
          )}
          <div className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className={cn(isDark && 'border-slate-700 text-slate-300 hover:bg-slate-800')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrantClick}
              disabled={isSaving || !dateValue}
              className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
              {hasPremium ? 'Update end date' : 'Grant premium'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
